import './App.css';
import { useState, useMemo, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import Home from './pages/Home/Home';
import { Cles, Ethanol, Diagnostic } from './pages/Services/Services';
import Quote from './pages/Quote/Quote';
import Contact from './pages/Contact/Contact';
import Account from './pages/Account/Account';
import Admin from './pages/Admin/Admin';
import Booking from './pages/Booking/Booking';
import Footer from './components/Footer/Footer';
import Navbar from './components/Navbar/Navbar';

import pb from './lib/pocketbase';

// Helper component to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);
  return null;
};

function App() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);


  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const installPWA = () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then((choiceResult) => {
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
      }
      setDeferredPrompt(null);
    });
  };

  const [vehicle, setVehicle] = useState({
    plate: '',
    brand: '',
    model: '',
    year: '',
    fuel: 'essence',
    vehicle_type: 'citadine',
  });

  const [selectedServices, setSelectedServices] = useState({
    urgent: false,
  });

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    setVehicle((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (e) => {
    const { name, checked, id } = e.target;
    const key = id || name; // Allow both name or ID (for dynamic services)
    setSelectedServices((prev) => ({ ...prev, [key]: checked }));
  };

  const [customer, setCustomer] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    city: '',
    notes: '',
    password: '',
    passwordConfirm: '',
    createAccount: true,
  });

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const [dbServices, setDbServices] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [agencySettings, setAgencySettings] = useState(null);

  useEffect(() => {
    const fetchDbData = async () => {
      try {
        const [servicesRes, rulesRes, settingsRes] = await Promise.all([
          pb.collection('services').getFullList({
            sort: 'category,name',
            filter: 'published != false',
          }),
          pb.collection('pricing_rules').getFullList({
            sort: 'priority',
          }).catch(() => []), // Fallback if collection doesn't exist yet
          pb.collection('company_settings').getList(1, 1).catch(() => ({ items: [] }))
        ]);
        setDbServices(servicesRes);
        setPricingRules(rulesRes);
        if (settingsRes.items.length > 0) {
          setAgencySettings(settingsRes.items[0]);
        }
      } catch (err) {
        console.error('Error fetching db data:', err);
      }
    };
    fetchDbData();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();

  // Compatibility shim if needed, or we just refactor. 
  // Let's refactor the main Page state but keep the setCurrentPage prop for now as a wrapper to navigate
  const setCurrentPage = (page) => {
    const routeMap = {
      'accueil': '/',
      'cles': '/cles',
      'ethanol': '/ethanol',
      'diagnostic': '/diagnostic',
      'devis': '/devis',
      'contact': '/contact',
      'booking': '/booking',
      'account': '/account',
      'admin': '/admin'
    };
    navigate(routeMap[page] || '/');
  };

  const currentPage = location.pathname === '/' ? 'accueil' : location.pathname.split('/')[1];

  const [navOpen, setNavOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('dark');
  const [showTour, setShowTour] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [tourStyle, setTourStyle] = useState({});
  const [spotlightStyle, setSpotlightStyle] = useState({});
  const [tourPlacement, setTourPlacement] = useState('top');

  useEffect(() => {
    if (!showTour) return;

    const updateTourPosition = () => {
      let targetId = '';
      if (tourStep === 0) targetId = 'tour-step-plate';
      else if (tourStep === 1) targetId = 'tour-step-services';
      else if (tourStep === 2) targetId = 'tour-step-cta';
      else if (tourStep === 3) targetId = 'tour-step-theme';

      const el = document.getElementById(targetId);
      if (el) {
        const rect = el.getBoundingClientRect();
        let style = {};

        if (tourStep === 3) {
          // Theme toggle in navbar - place below and shifted left
          style = {
            top: `${rect.bottom + 25}px`,
            left: `${rect.left + rect.width / 2}px`,
            transform: 'translateX(-85%)'
          };
          setTourPlacement('bottom');
        } else {
          // Standard elements
          // Place above by default, unless too close to top
          if (rect.top < 250) {
            // Place below
            style = {
              top: `${rect.bottom + 25}px`,
              left: `${rect.left + rect.width / 2}px`,
              transform: 'translateX(-50%)'
            };
            setTourPlacement('bottom');
          } else {
            // Place above
            style = {
              top: `${rect.top - 25}px`,
              left: `${rect.left + rect.width / 2}px`,
              transform: 'translate(-50%, -100%)'
            };
            setTourPlacement('top');
          }
        }
        setTourStyle(style);

        // Calculate spotlight
        setSpotlightStyle({
          top: `${rect.top}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          borderRadius: window.getComputedStyle(el).borderRadius || '8px'
        });
      }
    };

    // Calculate immediately and on events
    // Small timeout to ensure render is complete (especially for modal transitions)
    const timer = setTimeout(updateTourPosition, 100);

    window.addEventListener('resize', updateTourPosition);
    window.addEventListener('scroll', updateTourPosition);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTourPosition);
      window.removeEventListener('scroll', updateTourPosition);
    };
  }, [showTour, tourStep, currentPage]);

  // Auth features
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [user, setUser] = useState(null); // null = not logged in, object = logged in
  const [authForm, setAuthForm] = useState({ email: '', password: '', name: '' });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [authError, setAuthError] = useState('');

  const [userVehicles, setUserVehicles] = useState([]);

  // Quote Confirmation Modal
  const [showQuoteConfirmation, setShowQuoteConfirmation] = useState(false);
  const [quoteConfirmationData, setQuoteConfirmationData] = useState(null);
  const [quoteError, setQuoteError] = useState(null);

  // Fetch user vehicles for selector in Quote/Account
  useEffect(() => {
    if (user) {
      const fetchUserVehicles = async () => {
        try {
          const records = await pb.collection('vehicles').getFullList({
            filter: `users = "${user.id}"`,
            sort: '-created',
          });
          setUserVehicles(records);
        } catch (e) {
          console.error(e);
        }
      };
      fetchUserVehicles();
    } else {
      setUserVehicles([]);
    }
  }, [user]);

  // Continuous Confetti Effect when Modal is Open
  useEffect(() => {
    let intervalId;
    if (showQuoteConfirmation) {
      // Initial Big Burst
      confetti({
        zIndex: 10000,
        particleCount: 150,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#2563eb', '#facc15', '#ffffff']
      });

      // Continuous Fireworks loop
      intervalId = setInterval(() => {
        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        confetti({
          zIndex: 10000,
          particleCount: 50,
          startVelocity: 30,
          spread: 360,
          origin: {
            x: randomInRange(0.1, 0.9),
            y: Math.random() - 0.2
          },
          colors: ['#2563eb', '#facc15', '#ffffff', '#22c55e'], // Blue, Yellow, White, Green
          disableForReducedMotion: true
        });
      }, 3000); // Every 3 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [showQuoteConfirmation]);

  const handleSelectVehicle = (vId) => {
    const v = userVehicles.find(item => item.id === vId);
    if (v) {
      setVehicle({
        plate: v.plate,
        brand: v.brand,
        model: v.model,
        year: v.year,
        fuel: v.fuel.toLowerCase(),
      });
    }
  };

  // Check session on mount
  useEffect(() => {
    if (pb.authStore.isValid) {
      setUser(pb.authStore.model);
    }
  }, []);

  // Pre-fill customer info when user is logged in
  useEffect(() => {
    if (user) {
      setCustomer(prev => ({
        ...prev,
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ').slice(1).join(' ') || '',
        email: user.email || '',
        phone: user.phone || '',
        city: user.city || '',
      }));
    }
  }, [user]);

  const handleLogout = () => {
    pb.authStore.clear();
    setUser(null);
    setShowUserMenu(false);
  };


  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      if (authMode === 'login') {
        const authData = await pb.collection('users').authWithPassword(authForm.email, authForm.password);
        setUser(authData.record);
      } else {
        const data = {
          email: authForm.email,
          password: authForm.password,
          passwordConfirm: authForm.password,
          name: authForm.name,
        };
        await pb.collection('users').create(data);
        // Auto login after register
        const authData = await pb.collection('users').authWithPassword(authForm.email, authForm.password);
        setUser(authData.record);
      }
      setShowAuthModal(false);
      setAuthForm({ email: '', password: '', name: '' });
    } catch (err) {
      let friendlyMessage = 'Une erreur est survenue lors de l\'authentification';
      const errorMessage = err.message.toLowerCase();

      if (errorMessage.includes('fail') || errorMessage.includes('invalid credentials')) {
        friendlyMessage = "Oups ! L’email ou le mot de passe est incorrect. Réessaye ou réinitialise ton mot de passe.";
      } else if (errorMessage.includes('identity already exists')) {
        friendlyMessage = "Cet email est déjà utilisé. Essaye de te connecter plutôt !";
      } else if (errorMessage.includes('at least 8 characters')) {
        friendlyMessage = "Ton mot de passe doit faire au moins 8 caractères pour être bien sécurisé.";
      } else if (errorMessage.includes('not a valid email')) {
        friendlyMessage = "L'adresse email saisie ne semble pas valide.";
      }

      setAuthError(friendlyMessage || err.message);
    }
  };


  const priceDetails = useMemo(() => {
    const lines = [];
    const urgentMultiplier = selectedServices.urgent ? 1.2 : 1;
    let totalBase = 0;
    const activeSurcharges = [];

    // Helper to evaluate a dynamic rule
    const evaluateRule = (currentPrice, rule) => {
      const { type, operator, condition, value, is_percentage, label } = rule;
      let targetValue;

      switch (type) {
        case 'annee': targetValue = parseInt(vehicle.year); break;
        case 'marque': targetValue = vehicle.brand.toLowerCase(); break;
        case 'modele': targetValue = vehicle.model.toLowerCase(); break;
        case 'carburant': targetValue = vehicle.fuel; break;
        case 'vehicle_type': targetValue = vehicle.vehicle_type; break;
        default: return null;
      }

      let match = false;
      const cond = condition.toLowerCase();

      if (operator === '=') match = String(targetValue) === cond;
      else if (operator === 'contains') match = String(targetValue).includes(cond);
      else if (operator === '>=') match = targetValue >= parseInt(cond);
      else if (operator === '<=') match = targetValue <= parseInt(cond);
      else if (operator === 'between') {
        const [min, max] = cond.split('-').map(v => parseInt(v.trim()));
        match = targetValue >= min && targetValue <= max;
      }

      if (match) {
        const amount = is_percentage ? currentPrice * (value / 100) : value;
        return { amount, label: label || `${type} ${condition}` };
      }
      return null;
    };

    dbServices.forEach(srv => {
      if (selectedServices[srv.id]) {
        const base = srv.base_price || 0;
        totalBase += base;

        let adjustedPrice = base;
        pricingRules.forEach(rule => {
          const adjustment = evaluateRule(base, rule);
          if (adjustment) {
            adjustedPrice += adjustment.amount;
            // Collect surcharges for the global display
            const existing = activeSurcharges.find(s => s.label === adjustment.label);
            if (existing) existing.amount += adjustment.amount;
            else activeSurcharges.push({ label: adjustment.label, amount: adjustment.amount });
          }
        });

        const finalPrice = Math.round(adjustedPrice * urgentMultiplier);
        lines.push({
          label: srv.name,
          price: finalPrice,
          id: srv.id
        });
      }
    });

    const total = lines.reduce((sum, line) => sum + line.price, 0);

    return { lines, total, totalBase, activeSurcharges };
  }, [selectedServices, dbServices, pricingRules, vehicle]);




  useEffect(() => {
    try {
      const stored = window.localStorage.getItem('universdiag_theme');
      const tourDone = window.localStorage.getItem('universdiag_tour_done') === '1';

      if (stored === 'light' || stored === 'dark') {
        setTheme(stored);
        setSelectedTheme(stored);
        if (!tourDone) {
          setShowTour(true);
          setTourStep(0);
        }
      } else {
        // Sophisticated detection: Use system preference as default
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setSelectedTheme(prefersDark ? 'dark' : 'light');
        setShowThemeModal(true);
      }
    } catch (e) {
      setShowThemeModal(true);
    }
  }, []);

  // Bloque le scroll de la page quand un overlay important est affiché (thème ou tour)
  useEffect(() => {
    const modalActive = showThemeModal || (showTour && currentPage === 'accueil');
    if (modalActive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [showThemeModal, showTour, currentPage]);

  // Centre la vue sur la bonne zone pendant le tour guidé
  useEffect(() => {
    if (!showTour || currentPage !== 'accueil') return;
    let top = 0;
    if (tourStep === 0) {
      top = 70; // plaque
    } else if (tourStep === 1) {
      top = 170; // prestations
    } else if (tourStep === 2) {
      top = 240; // bouton CTA
    } else if (tourStep === 3) {
      top = 0; // bouton thème dans le header
    }
    window.scrollTo({ top, behavior: 'smooth' });
  }, [showTour, tourStep, currentPage]);

  const handleSubmit = async (e, mode = 'quote', extraData = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    setQuoteError(null); // Reset error

    // Check local user state vs SDK store (only if supposedly logged in)
    if (user && !pb.authStore.isValid) {
      setQuoteError("Votre session a expirée ou est invalide. Veuillez vous reconnecter.");
      setUser(null);
      setShowAuthModal(true);
      return;
    }

    let userId = user ? user.id : null;

    // Handle inline account creation for guests
    if (!user && customer.createAccount) {
      if (!customer.password || customer.password.length < 8) {
        setQuoteError({ message: "Veuillez définir un mot de passe d'au moins 8 caractères pour créer votre compte.", field: "password" });
        return;
      }
      if (customer.password !== customer.passwordConfirm) {
        setQuoteError({ message: "Les mots de passe ne correspondent pas.", field: "passwordConfirm" });
        return;
      }

      try {
        const userData = {
          email: customer.email,
          emailVisibility: true,
          password: customer.password,
          passwordConfirm: customer.passwordConfirm,
          name: `${customer.firstName} ${customer.lastName}`.trim(),
          firstName: customer.firstName,
          lastName: customer.lastName,
          phone: customer.phone,
          city: customer.city
        };

        const newRecord = await pb.collection('users').create(userData);
        await pb.collection('users').authWithPassword(customer.email, customer.password);

        userId = newRecord.id;
        setUser(pb.authStore.model);
      } catch (err) {
        console.error("Erreur création compte:", err);
        let msg = err.message;
        if (err.data && err.data.email) msg = "Cet email est déjà utilisé.";
        setQuoteError({ message: "Impossible de créer le compte : " + msg, field: "email" });
        return;
      }
    }

    try {
      // Validate/sanitize year
      const safeYear = parseInt(vehicle.year);
      const payloadYear = !isNaN(safeYear) ? safeYear : null;

      // Validate/sanitize total
      const safeTotal = parseFloat(priceDetails.total);
      const payloadTotal = !isNaN(safeTotal) ? safeTotal : 0;

      const data = {
        users: userId,
        vehicle_plate: vehicle.plate,
        vehicle_brand: vehicle.brand,
        vehicle_model: vehicle.model,
        vehicle_year: payloadYear,
        vehicle_fuel: vehicle.fuel,
        // Legacy flags (best effort)
        service_key: !!dbServices.find(s => selectedServices[s.id] && s.category === 'Clés'),
        service_ethanol: !!dbServices.find(s => selectedServices[s.id] && s.category === 'Éthanol'),
        service_diagnostic: !!dbServices.find(s => selectedServices[s.id] && s.category === 'Diagnostic'),
        service_urgent: !!selectedServices.urgent,
        // Detailed services
        services_list: dbServices.filter(s => selectedServices[s.id]).map(s => s.name).join(', '),
        total_price: payloadTotal,
        status: 'en_attente',
        vehicle_type: vehicle.vehicle_type,
        firstName: customer.firstName,
        lastName: customer.lastName,
        phone: customer.phone,
        email: customer.email,
        city: customer.city,
        notes: customer.notes
      };

      // console.log('Envoi du devis (payload):', data);

      await pb.collection('quotes').create(data);

      // If mode is booking, also create the appointment
      if (mode === 'booking' && extraData.date && extraData.time) {
        const appointmentData = {
          users: userId,
          service: data.services_list.substring(0, 100), // Ensure it fits if long
          date: extraData.date,
          time: extraData.time,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          vehicle_details: `${data.vehicle_brand} ${data.vehicle_model}`,
          status: 'pending'
        };
        await pb.collection('appointments').create(appointmentData);
      }

      // Instead of simple alert, trigger confirmation modal
      setQuoteConfirmationData({
        id: userId ? 'saved-in-account' : 'guest',
        email: customer.email,
        mode: mode // Store the mode in confirmation
      });
      setShowQuoteConfirmation(true);
    } catch (err) {
      console.error('Erreur lors de l’enregistrement du devis:', err);

      let errorMessage = err.message || 'Impossible d’enregistrer le devis.';
      const responseData = err.response?.data || err.data;
      const validationErrors = responseData?.data || responseData;

      // Handle specific case where user ID is invalid/not found (stale session)
      if (validationErrors?.users?.code === 'validation_missing_rel_records') {
        setQuoteError("Votre session utilisateur semble invalide ou expirée. Vous allez être déconnecté pour vous reconnecter proprement.");
        handleLogout();
        setShowAuthModal(true);
        return;
      }

      if (validationErrors && typeof validationErrors === 'object') {
        try {
          const lines = [];
          for (const [key, val] of Object.entries(validationErrors)) {
            const msg = typeof val === 'object' && val.message ? val.message : JSON.stringify(val);
            // Translate specific fields if possible, or just use key
            const label = key === 'vehicle_plate' ? 'Immatriculation' : key;
            lines.push(`${label}: ${msg}`);
          }
          if (lines.length > 0) errorMessage = lines.join('\n');
        } catch (e) {
          errorMessage += `\n\nRaw Data: ${JSON.stringify(validationErrors)}`;
        }
      }

      setQuoteError(errorMessage);
    }
  };

  const closeQuoteConfirmation = () => {
    setShowQuoteConfirmation(false);
    if (quoteConfirmationData?.id === 'saved-in-account') {
      setCurrentPage('account');
    } else {
      setCurrentPage('accueil');
    }
    setQuoteConfirmationData(null);
  };

  const generateQuotePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    const companyName = agencySettings?.company_name || "L'Univers Diag";
    const phone = agencySettings?.phone_public || "07 58 04 43 77";

    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235); // Blue primary
    doc.text(companyName, 20, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Atelier & Ingénierie Automobile", 20, 26);
    doc.text(`Toulouse - ${phone}`, 20, 31);

    // Title
    doc.setFontSize(16);
    doc.setTextColor(0);
    doc.text("Devis Estimatif", pageWidth - 20, 25, { align: 'right' });
    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - 20, 31, { align: 'right' });

    // Customer & Vehicle Info
    doc.setDrawColor(200);
    doc.line(20, 38, pageWidth - 20, 38);

    doc.setFontSize(11);
    doc.text("Client :", 20, 48);
    doc.setFont("helvetica", "bold");
    doc.text(`${customer.firstName} ${customer.lastName}`, 20, 53);
    doc.setFont("helvetica", "normal");
    doc.text(customer.email, 20, 58);
    if (customer.phone) doc.text(customer.phone, 20, 63);

    doc.text("Véhicule :", pageWidth / 2, 48);
    doc.setFont("helvetica", "bold");
    doc.text(`${vehicle.brand} ${vehicle.model}`, pageWidth / 2, 53);
    doc.setFont("helvetica", "normal");
    doc.text(`Immatriculation : ${vehicle.plate}`, pageWidth / 2, 58);
    if (vehicle.year) doc.text(`Année : ${vehicle.year} - ${vehicle.fuel}`, pageWidth / 2, 63);

    doc.line(20, 70, pageWidth - 20, 70);

    // Services Table
    const tableRows = [];
    priceDetails.lines.forEach(line => {
      tableRows.push([line.label, `${line.price} €`]);
    });

    if (priceDetails.activeSurcharges.length > 0) {
      priceDetails.activeSurcharges.forEach(s => {
        tableRows.push([`+ ${s.label}`, `${Math.round(s.amount)} €`]);
      });
    }

    // Total Row
    tableRows.push(['', '']);

    autoTable(doc, {
      startY: 80,
      head: [['Prestation', 'Prix']],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235] },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { cellWidth: 40, halign: 'right' },
      },
      foot: [['TOTAL ESTIMÉ', `${priceDetails.total} €`]],
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' }
    });

    // Disclaimer
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text("* Ce devis est une estimation indicative. Le prix final sera confirmé lors du rendez-vous.", 20, finalY);
    doc.text("* Tarifs TTC. Frais de déplacement non inclus.", 20, finalY + 5);

    // Save
    doc.save(`Devis_LUniversDiag_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem('universdiag_theme', next);
      } catch (e) {
        // ignore
      }
      return next;
    });
  };

  const renderAuthModal = () => (
    <div className="modal-backdrop" onClick={() => setShowAuthModal(false)}>
      <div className="modal-content auth-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={() => setShowAuthModal(false)}>×</button>
        <div className="auth-header">
          <div className="auth-icon-large">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
          </div>
          <h2>{authMode === 'login' ? 'Connexion' : 'Créer un compte'}</h2>
          <p>{authMode === 'login' ? 'Accède à ton espace client' : 'Rejoins L\'Univers Diag'}</p>
        </div>

        <form onSubmit={handleAuthSubmit} className="auth-form">
          {authError && <div className="auth-error-message">{authError}</div>}
          {authMode === 'register' && (
            <div className="form-group">
              <label>Nom complet</label>
              <input
                type="text"
                placeholder="Ex: Jean Dupont"
                value={authForm.name}
                onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                required
              />
            </div>
          )}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="Ex: jean.dupont@email.com"
              value={authForm.email}
              onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Mot de passe</label>
            <input
              type="password"
              placeholder="••••••••"
              value={authForm.password}
              onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
              required
            />
          </div>

          <button type="submit" className="primary-button full-width">
            {authMode === 'login' ? 'Se connecter' : 'S\'inscrire'}
          </button>
        </form>

        <div className="auth-footer">
          {authMode === 'login' ? (
            <p>Pas encore de compte ? <button onClick={() => setAuthMode('register')}>Créer un compte</button></p>
          ) : (
            <p>Déjà un compte ? <button onClick={() => setAuthMode('login')}>Se connecter</button></p>
          )}
        </div>
      </div>
    </div>
  );



  return (
    <div className={`app-root ${theme}`}>
      <ScrollToTop />

      {currentPage !== 'admin' && (
        <Navbar
          user={user}
          showUserMenu={showUserMenu}
          setShowUserMenu={setShowUserMenu}
          setShowAuthModal={setShowAuthModal}
          handleLogout={handleLogout}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          navOpen={navOpen}
          setNavOpen={setNavOpen}
          toggleTheme={toggleTheme}
          theme={theme}
          showTour={showTour}
          tourStep={tourStep}
          agencySettings={agencySettings}
        />
      )}

      <main className={`app-main ${(currentPage === 'account' || currentPage === 'admin' || currentPage === 'devis' || currentPage === 'cles' || currentPage === 'ethanol' || currentPage === 'diagnostic' || currentPage === 'booking') ? 'full-width' : ''}`}>
        <Routes>
          <Route path="/" element={
            <Home
              vehicle={vehicle}
              services={selectedServices}
              dbServices={dbServices}
              pricingRules={pricingRules}
              priceDetails={priceDetails}
              customer={customer}
              user={user}
              showTour={showTour}
              tourStep={tourStep}
              handleVehicleChange={handleVehicleChange}
              handleServiceChange={handleServiceChange}
              handleCustomerChange={handleCustomerChange}
              setCurrentPage={setCurrentPage}
              setShowAuthModal={setShowAuthModal}
              setAuthMode={setAuthMode}
              agencySettings={agencySettings}
            />
          } />
          <Route path="/cles" element={<Cles
            dbServices={dbServices}
            services={selectedServices}
            pricingRules={pricingRules}
            priceDetails={priceDetails}
            vehicle={vehicle}
            customer={customer}
            user={user}
            showTour={showTour}
            tourStep={tourStep}
            handleVehicleChange={handleVehicleChange}
            handleServiceChange={handleServiceChange}
            handleCustomerChange={handleCustomerChange}
            setCurrentPage={setCurrentPage}
            setShowAuthModal={setShowAuthModal}
            setAuthMode={setAuthMode}
          />} />
          <Route path="/ethanol" element={<Ethanol
            dbServices={dbServices}
            services={selectedServices}
            pricingRules={pricingRules}
            priceDetails={priceDetails}
            vehicle={vehicle}
            customer={customer}
            user={user}
            showTour={showTour}
            tourStep={tourStep}
            handleVehicleChange={handleVehicleChange}
            handleServiceChange={handleServiceChange}
            handleCustomerChange={handleCustomerChange}
            setCurrentPage={setCurrentPage}
            setShowAuthModal={setShowAuthModal}
            setAuthMode={setAuthMode}
          />} />
          <Route path="/diagnostic" element={<Diagnostic
            dbServices={dbServices}
            services={selectedServices}
            pricingRules={pricingRules}
            priceDetails={priceDetails}
            vehicle={vehicle}
            customer={customer}
            user={user}
            showTour={showTour}
            tourStep={tourStep}
            handleVehicleChange={handleVehicleChange}
            handleServiceChange={handleServiceChange}
            handleCustomerChange={handleCustomerChange}
            setCurrentPage={setCurrentPage}
            setShowAuthModal={setShowAuthModal}
            setAuthMode={setAuthMode}
          />} />
          <Route path="/devis" element={
            <Quote
              vehicle={vehicle}
              services={selectedServices}
              dbServices={dbServices}
              pricingRules={pricingRules}
              customer={customer}
              priceDetails={priceDetails}
              userVehicles={userVehicles}
              handleVehicleChange={handleVehicleChange}
              handleSelectVehicle={handleSelectVehicle}
              handleServiceChange={handleServiceChange}
              handleCustomerChange={handleCustomerChange}
              handleSubmit={handleSubmit}
              user={user}
              quoteError={quoteError}
              showTour={showTour}
              tourStep={tourStep}
              setCurrentPage={setCurrentPage}
              setShowAuthModal={setShowAuthModal}
              setAuthMode={setAuthMode}
            />
          } />
          <Route path="/booking" element={
            <Booking
              vehicle={vehicle}
              services={selectedServices}
              dbServices={dbServices}
              pricingRules={pricingRules}
              customer={customer}
              priceDetails={priceDetails}
              handleVehicleChange={handleVehicleChange}
              handleServiceChange={handleServiceChange}
              handleCustomerChange={handleCustomerChange}
              handleSubmit={handleSubmit}
              user={user}
              quoteError={quoteError}
              setCurrentPage={setCurrentPage}
              setShowAuthModal={setShowAuthModal}
              setAuthMode={setAuthMode}
              agencySettings={agencySettings}
            />
          } />
          <Route path="/contact" element={<Contact agencySettings={agencySettings} />} />
          <Route path="/account" element={
            <Account
              user={user}
              authForm={authForm}
              vehicle={vehicle}
              setCurrentPage={setCurrentPage}
              onNavigateToDevis={() => navigate('/devis')}
            />
          } />
          <Route path="/admin/*" element={
            user?.role === 'admin' ? (
              <Admin setCurrentPage={setCurrentPage} />
            ) : (
              <div className="admin-content-wrapper" style={{ textAlign: 'center', padding: '5rem' }}>
                <h2>Accès Refusé</h2>
                <p>Vous n'avez pas les permissions pour accéder à cet espace.</p>
                <button className="primary-button" onClick={() => navigate('/')}>Retour à l'accueil</button>
              </div>
            )
          } />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>

      {(currentPage !== 'admin' && currentPage !== 'account') && (
        <Footer
          setCurrentPage={setCurrentPage}
          setShowTour={setShowTour}
          setTourStep={setTourStep}
          installPWA={installPWA}
          canInstall={!!deferredPrompt}
          agencySettings={agencySettings}
        />
      )}

      {showThemeModal && (
        <div className="theme-modal-backdrop">
          <div className="theme-modal-premium">
            <div className="theme-modal-header">
              <h2>Bienvenue chez L'Univers Diag</h2>
              <p>Choisissez un thème pour commencer votre expérience</p>
            </div>

            <div className="theme-cards-container">
              <div
                className={`theme-card-premium light ${selectedTheme === 'light' ? 'active' : ''}`}
                onClick={() => {
                  const newTheme = 'light';
                  setTheme(newTheme);
                  setSelectedTheme(newTheme);
                  // Instant save and close with micro-delay for feedback
                  try {
                    window.localStorage.setItem('universdiag_theme', newTheme);
                    setTimeout(() => {
                      const tourDone = window.localStorage.getItem('universdiag_tour_done') === '1';
                      if (!tourDone) {
                        setShowTour(true);
                        setTourStep(0);
                      }
                      setShowThemeModal(false);
                    }, 400);
                  } catch (e) {
                    setShowThemeModal(false);
                  }
                }}
                onMouseEnter={() => setTheme('light')}
                onMouseLeave={() => !window.localStorage.getItem('universdiag_theme') && setTheme(selectedTheme)}
              >
                <div className="card-visual">
                  <div className="sun-icon-animated">
                    <div className="sun-core"></div>
                    <div className="sun-rays">
                      {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <span key={i} style={{ '--i': i }}></span>)}
                    </div>
                  </div>
                </div>
                <div className="card-info">
                  <span className="theme-name">Mode Clair</span>
                  <p>Épuré & Lumineux</p>
                </div>
                <div className="selection-badge">✓</div>
              </div>

              <div
                className={`theme-card-premium dark ${selectedTheme === 'dark' ? 'active' : ''}`}
                onClick={() => {
                  const newTheme = 'dark';
                  setTheme(newTheme);
                  setSelectedTheme(newTheme);
                  // Instant save and close with micro-delay for feedback
                  try {
                    window.localStorage.setItem('universdiag_theme', newTheme);
                    setTimeout(() => {
                      const tourDone = window.localStorage.getItem('universdiag_tour_done') === '1';
                      if (!tourDone) {
                        setShowTour(true);
                        setTourStep(0);
                      }
                      setShowThemeModal(false);
                    }, 400);
                  } catch (e) {
                    setShowThemeModal(false);
                  }
                }}
                onMouseEnter={() => setTheme('dark')}
                onMouseLeave={() => !window.localStorage.getItem('universdiag_theme') && setTheme(selectedTheme)}
              >
                <div className="card-visual">
                  <div className="moon-icon-animated">
                    <div className="moon-body"></div>
                    <div className="stars-twinkle">
                      <span></span><span></span><span></span>
                    </div>
                  </div>
                </div>
                <div className="card-info">
                  <span className="theme-name">Mode Sombre</span>
                  <p>Élégant & Confortable</p>
                </div>
                <div className="selection-badge">✓</div>
              </div>
            </div>

            <div className="theme-modal-footer-premium" style={{ justifyContent: 'center' }}>
              <button
                type="button"
                className="btn-skip-theme"
                onClick={() => setShowThemeModal(false)}
              >
                Décider plus tard
              </button>
            </div>
          </div>
        </div>
      )}

      {showTour && currentPage === 'accueil' && !showThemeModal && (
        <div className="tour-overlay">
          <div className="tour-spotlight" style={spotlightStyle}></div>
          <div
            className={`tour-tooltip step-${tourStep === 0 ? 'plate' : tourStep === 1 ? 'services' : tourStep === 2 ? 'cta' : 'theme'} pos-${tourPlacement}`}
            style={tourStyle}
          >
            {tourStep === 0 && (
              <>
                <p className="tour-title">Étape 1 : ta plaque</p>
                <p>Saisis ici la plaque d&apos;immatriculation de ton véhicule. C&apos;est la base de ta demande.</p>
              </>
            )}
            {tourStep === 1 && (
              <>
                <p className="tour-title">Étape 2 : prestations</p>
                <p>Coche une ou plusieurs prestations : clé, reprogrammation éthanol, diagnostic électronique.</p>
              </>
            )}
            {tourStep === 2 && (
              <>
                <p className="tour-title">Étape 3 : demander un devis</p>
                <p>Clique sur &quot;Demander un devis&quot; pour aller au formulaire avec les infos déjà pré-remplies.</p>
              </>
            )}
            {tourStep === 3 && (
              <>
                <p className="tour-title">Étape 4 : changer de thème</p>
                <p>
                  Ici tu peux passer du thème sombre au thème clair (et inversement) à tout moment pendant ta
                  navigation.
                </p>
              </>
            )}
            <div className="tour-footer">
              <button
                type="button"
                className="tour-skip"
                onClick={() => {
                  setShowTour(false);
                  try {
                    window.localStorage.setItem('universdiag_tour_done', '1');
                  } catch (e) {
                    // ignore
                  }
                }}
              >
                Ignorer
              </button>
              {tourStep < 3 ? (
                <button
                  type="button"
                  className="tour-next"
                  onClick={() => setTourStep((step) => Math.min(step + 1, 3))}
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="button"
                  className="tour-next"
                  onClick={() => {
                    setShowTour(false);
                    try {
                      window.localStorage.setItem('universdiag_tour_done', '1');
                    } catch (e) {
                      // ignore
                    }
                  }}
                >
                  Terminer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showAuthModal && renderAuthModal()}

      {showQuoteConfirmation && (
        <div className="modal-backdrop" onClick={closeQuoteConfirmation}>
          <div className="modal-content confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-icon">
              ✅
            </div>
            <h2>{quoteConfirmationData?.mode === 'booking' ? 'Rendez-vous réservé !' : 'Demande reçue !'}</h2>
            <p style={{ textAlign: 'center', color: '#94a3b8', marginTop: '0.5rem' }}>
              {quoteConfirmationData?.mode === 'booking' 
                ? <>Merci <strong>{customer.firstName}</strong>, votre rendez-vous est bien enregistré dans notre agenda.</>
                : <>Merci <strong>{customer.firstName}</strong>, votre demande de devis a bien été transmise à l'atelier.</>
              }
            </p>

            <div className="confirmation-actions-group" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem', width: '100%' }}>
              {/* 1. Download PDF */}
              <button
                className="secondary-button full-width"
                onClick={generateQuotePDF}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Télécharger mon devis (PDF)
              </button>

              <div className="confirmation-details" style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#64748b' }}>
                <p>📧 Un récapitulatif a aussi été envoyé à : <strong>{quoteConfirmationData?.email}</strong></p>
              </div>

              {/* 2. Account Actions */}
              {quoteConfirmationData?.id === 'saved-in-account' ? (
                <>
                  <div className="success-notice" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', padding: '0.75rem', borderRadius: '0.5rem', fontSize: '0.9rem', textAlign: 'center', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
                    {quoteConfirmationData?.mode === 'booking' 
                      ? '📅 RDV enregistré dans votre espace client.'
                      : '📂 Devis enregistré dans votre espace client.'
                    }
                  </div>
                  <button className="primary-button full-width" onClick={() => { setShowQuoteConfirmation(false); setCurrentPage('account'); }}>
                    {quoteConfirmationData?.mode === 'booking' ? 'Voir mon planning' : 'Voir mes devis'}
                  </button>
                </>
              ) : (
                <>
                  <div className="info-notice" style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', padding: '0.75rem', borderRadius: '0.5rem', fontSize: '0.9rem', textAlign: 'left' }}>
                    🚀 <strong>Astuce :</strong> Créez un compte maintenant pour retrouver ce devis plus tard et suivre son avancement !
                  </div>
                  <button
                    className="primary-button full-width"
                    onClick={() => {
                      setShowQuoteConfirmation(false);
                      setAuthMode('register');
                      setShowAuthModal(true);
                    }}
                  >
                    Créer un compte maintenant
                  </button>
                  <button
                    className="text-link-button"
                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.85rem' }}
                    onClick={closeQuoteConfirmation}
                  >
                    Non merci, retourner à l&apos;accueil
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default App;
